import { LitElement, html } from 'lit';
import styles from './fit-tooltip.css';

const positionAlternatives = {
  bottom: ['top', 'right', 'left'],
  top: ['bottom', 'right', 'left'],
  right: ['left', 'bottom', 'top'],
  left: ['right', 'bottom', 'top'],
};

/**
 * Show a tooltip when another element is hovered.
 *
 *  <code>
 *    &lt;p class=&quot;text--mouseover&quot;&gt;If you hover &lt;span aria-describedby=&quot;mouseoverTooltip&quot; class=&quot;text--mouseover-target&quot;&gt;this&lt;/span&gt; text...
 *      &lt;fit-tooltip id=&quot;mouseoverTooltip&quot; role=&quot;tooltip&quot; tabindex=&quot;-1&quot; class=&quot;&quot; style=&quot;left: -244.297px; top: 35px; width: 776px;&quot; hidden=&quot;&quot;&gt;You will see a tooltip&lt;/fit-tooltip&gt;
 *    &lt;/p&gt;
 *  </code>
 *
 * @element fit-tooltip
 */
export class FitTooltip extends LitElement {
  static get is() { return 'fit-tooltip'; }

  static get styles() { return styles; }

  render() {
    return html`
      <div id="tooltip" class="hidden">
        <slot></slot>
      </div>
    `;
  }

  static get properties() {
    return {
      /**
       * Set the tooltip position.
       * Possible values are 'bottom', 'top', 'left' and 'right'.
       * Default value is 'bottom'
       */
      position: {
        type: String,
      },

      /**
       * If true, no parts of the tooltip will ever be shown offscreen.
       * Default value is 'true'
       */
      fitToVisibleBounds: {
        type: Boolean,
        attribute: 'fit-to-visible-bounds',
      },

      /**
       * The spacing between the top of the tooltip and the element it is
       * anchored to.
       * Default value is '14'(px)
       */
      offset: {
        type: Number,
      },
    };
  }

  constructor(props) {
    super(props);

    this.hidden = true;
    this.position = 'bottom';
    this.fitToVisibleBounds = true;
    this.offset = 14;

    this._show = this._show.bind(this);
    this._hide = this._hide.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute('role')) this.setAttribute('role', 'tooltip');

    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', -1);

    // We have to find the next id context, which may be a parent shadow root or document
    let parentNode = this;
    let parentDocument;
    while ((parentNode = parentNode.parentNode)) {
      parentDocument = parentNode;
    }

    // The element that triggers the tooltip references the tooltip
    // element with `aria-describedby`.
    this._target = (parentDocument || document).querySelector(`[aria-describedby=${this.id}]`);
    if (!this._target) return;

    this._target.addEventListener('focus', this._show);
    this._target.addEventListener('blur', this._hide);
    this._target.addEventListener('mouseenter', this._show);
    this._target.addEventListener('mouseleave', this._hide);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this._target) return;

    this._target.removeEventListener('focus', this._show);
    this._target.removeEventListener('blur', this._hide);
    this._target.removeEventListener('mouseenter', this._show);
    this._target.removeEventListener('mouseleave', this._hide);
    this._target = null;
  }

  firstUpdated(changedProperties) {
    this.$ = {
      tooltip: this.shadowRoot.querySelector('#tooltip'),
    };
  }

  updatePosition() {
    if (!this._target || !this.offsetParent) return;

    const parentRect = this.offsetParent.getBoundingClientRect();
    const targetRect = this._target.getBoundingClientRect();

    this.classList.add('invisible');
    const thisRect = this.getBoundingClientRect();
    this.classList.remove('invisible');

    const tooltipWidth = thisRect.width;
    const tooltipHeight = thisRect.height;

    let [tooltipLeft, tooltipTop] = this.calculatePosition(this.position, this.offset, thisRect, targetRect, parentRect);

    if (this.fitToVisibleBounds) {
      if (tooltipLeft < 0) {
        tooltipLeft = 0;
      }

      const alternativePositions = positionAlternatives[this.position].slice();
      let alternativePosition;
      while ((alternativePosition = alternativePositions.shift())) {
        if (parentRect.left + tooltipLeft + tooltipWidth > window.innerWidth || parentRect.top + tooltipTop + tooltipHeight
          > window.innerHeight) {
          [tooltipLeft, tooltipTop] = this.calculatePosition(alternativePosition, this.offset, thisRect, targetRect, parentRect);
        } else {
          break;
        }
      }
    }

    this.style.left = `${tooltipLeft}px`;
    this.style.top = `${tooltipTop}px`;
    this.style.width = `${tooltipWidth}px`;
  }

  calculatePosition(position, offset, thisRect, targetRect, parentRect) {
    const horizontalCenterOffset = (targetRect.width - thisRect.width) / 2;
    const verticalCenterOffset = (targetRect.height - thisRect.height) / 2;

    const targetLeft = targetRect.left - parentRect.left;
    const targetTop = targetRect.top - parentRect.top;

    let tooltipLeft; let
      tooltipTop;
    switch (position) {
      case 'top':
        tooltipLeft = targetLeft + horizontalCenterOffset;
        tooltipTop = targetTop - thisRect.height - offset;
        break;
      case 'bottom':
        tooltipLeft = targetLeft + horizontalCenterOffset;
        tooltipTop = targetTop + targetRect.height + offset;
        break;
      case 'left':
        tooltipLeft = targetLeft - thisRect.width - offset;
        tooltipTop = targetTop + verticalCenterOffset;
        break;
      case 'right':
        tooltipLeft = targetLeft + targetRect.width + offset;
        tooltipTop = targetTop + verticalCenterOffset;
        break;
    }
    return [tooltipLeft, tooltipTop];
  }

  _show() {
    if (this.innerHTML.trim() === '') {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    this.updatePosition();
    this.$.tooltip.classList.remove('hidden');
  }

  _hide() {
    this.hidden = true;
    this.$.tooltip.classList.add('hidden');
  }
}

window.customElements.define(FitTooltip.is, FitTooltip);
